import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from 'axios';
import {
  Typography, Box, Paper, Switch, CircularProgress, TextField,
  FormControlLabel, FormControl, RadioGroup, Radio, Tooltip, Button,
  MenuItem, Select,
} from "@mui/material";
import PortalPicker from './PortalPicker';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { WithContext as ReactTags } from 'react-tag-input';
import { useAuth } from '../context/AuthContext';
import { useScheduledSearch } from '../hooks/useScheduledSearches';
import { useNotification } from '../context/NotificationContext';

let apiPrefix = window.location.hostname === 'localhost' ? '' : '/api';

const KeyCodes = {
  comma: 188,
  enter: 13,
};
const delimiters = [KeyCodes.comma, KeyCodes.enter];

const SectionHeader = ({ title, tooltip }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1, mt: 4 }}>
    <Typography
      variant="h6"
      sx={{ fontWeight: 600, color: '#2c3e50', borderBottom: '2px solid #0079bf', pb: 0.5, fontSize: '1rem' }}
    >
      {title}
    </Typography>
    {tooltip && (
      <Tooltip title={tooltip} placement="right">
        <HelpOutlineIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
      </Tooltip>
    )}
  </Box>
);

const ScheduledSearchDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const userId = user?.user_id;
  const { showError, showSuccess } = useNotification();

  useEffect(() => { document.title = 'Uredi pretragu | Media Scanner'; }, []);

  const { data: searchData, isLoading, refetch } = useScheduledSearch(id);

  const [tags, setTags] = useState([]);
  const [excludingTags, setExcludingTags] = useState([]);
  const [emailTags, setEmailTags] = useState([]);
  const [selectedPortals, setSelectedPortals] = useState([]);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [frequency, setFrequency] = useState('daily');
  const [executionDay, setExecutionDay] = useState(0);
  const [executionTime, setExecutionTime] = useState('09:00');
  const [timeSpan, setTimeSpan] = useState(12);

  const dayLabels = ['Ponedjeljak', 'Utorak', 'Srijeda', 'Četvrtak', 'Petak', 'Subota', 'Nedjelja'];

  // Initial values for change detection
  const [initial, setInitial] = useState(null);

  // Sync all fields when searchData loads + capture initial snapshot
  useEffect(() => {
    if (!searchData) return;
    const initName = searchData.name || '';
    const initFrequency = searchData.frequency || 'daily';
    const initExecutionDay = searchData.execution_day ?? 0;
    const initTime = searchData.execution_time ? String(searchData.execution_time).slice(0, 5) : '09:00';
    const initTimeSpan = searchData.time_span ?? 12;
    const initTags = (searchData.terms || []).map(t => ({ id: String(t.id ?? t.text), text: String(t.text ?? t.name ?? '') }));
    const initExcluding = (searchData.excluding_terms || []).map(t => ({ id: String(t.id ?? t.text), text: String(t.text ?? t.name ?? '') }));
    const initPortals = (searchData.portals || []).map(p => ({ id: p.id, name: p.name ?? p.portal_name ?? '' }));
    const initEmails = (searchData.email_addresses || []).map(e => ({ id: e, text: e }));

    setName(initName);
    setFrequency(initFrequency);
    setExecutionDay(initExecutionDay);
    setExecutionTime(initTime);
    setTimeSpan(initTimeSpan);
    setTags(initTags);
    setExcludingTags(initExcluding);
    setSelectedPortals(initPortals);
    setEmailTags(initEmails);

    setInitial({
      name: initName,
      frequency: initFrequency,
      executionDay: initExecutionDay,
      executionTime: initTime,
      timeSpan: initTimeSpan,
      tags: JSON.stringify(initTags),
      excludingTags: JSON.stringify(initExcluding),
      portalIds: JSON.stringify(initPortals.map(p => p.id).sort()),
      emails: JSON.stringify(initEmails),
    });
  }, [searchData]);

  const handleToggle = async (search) => {
    try {
      const res = await axios.post(`${apiPrefix}/toggleScheduledSearch`, {
        id: search.id,
        is_active: !search.is_active,
        user_id: userId,
      }, { withCredentials: true });
      if (res.status === 200) {
        refetch();
      }
    } catch (err) {
      console.error("Greška prilikom toggle pretrage:", err);
    }
  };

  const handleDeleteTag = (i) => setTags(tags.filter((_, index) => index !== i));
  const handleAddition = (tag) => setTags([...tags, { id: String(tag.id ?? tag.text), text: String(tag.text) }]);
  const handleDrag = (tag, currPos, newPos) => {
    const next = tags.slice();
    next.splice(currPos, 1);
    next.splice(newPos, 0, tag);
    setTags(next);
  };

  const handleDeleteExcludingTag = (i) => setExcludingTags(excludingTags.filter((_, index) => index !== i));
  const handleAdditionExcluding = (tag) => setExcludingTags([...excludingTags, { id: String(tag.id ?? tag.text), text: String(tag.text) }]);

  const isFormValid = name.trim() && tags.length > 0 && selectedPortals.length > 0 && emailTags.length > 0;

  const hasChanges = initial ? (
    name !== initial.name ||
    frequency !== initial.frequency ||
    Number(executionDay) !== Number(initial.executionDay) ||
    executionTime !== initial.executionTime ||
    Number(timeSpan) !== Number(initial.timeSpan) ||
    JSON.stringify(tags) !== initial.tags ||
    JSON.stringify(excludingTags) !== initial.excludingTags ||
    JSON.stringify(selectedPortals.map(p => p.id).sort()) !== initial.portalIds ||
    JSON.stringify(emailTags) !== initial.emails
  ) : false;

  const handleSave = async () => {
    if (!name.trim()) { showError('Naziv pretrage je obavezno polje'); return; }
    if (tags.length === 0) { showError('Dodajte barem jedan pojam za pretragu'); return; }
    if (selectedPortals.length === 0) { showError('Odaberite barem jedan portal'); return; }
    if (emailTags.length === 0) { showError('Dodajte barem jednu email adresu za dostavu rezultata'); return; }
    setSaving(true);
    try {
      await axios.post(
        `${apiPrefix}/updateScheduledSearch`,
        {
          id: searchData?.id,
          user_id: userId,
          name: name.trim() || searchData?.name,
          frequency,
          execution_day: frequency === 'weekly' ? executionDay : null,
          execution_time: `${executionTime}:00`,
          time_span: Number(timeSpan),
        },
        { withCredentials: true }
      );
      await axios.post(
        `${apiPrefix}/checkAndSaveTermsForScheduledSearch`,
        {
          user_id: userId,
          scheduled_search_id: searchData?.id,
          terms: tags.map(t => ({
            id: String(t.id ?? t.text ?? ''),
            text: String(t.text ?? '')
          }))
        },
        { withCredentials: true }
      );
      await axios.post(
        `${apiPrefix}/checkAndSavePortalsForScheduledSearch`,
        {
          user_id: userId,
          scheduled_search_id: searchData?.id,
          portal_ids: selectedPortals.map(p => p.id),
        },
        { withCredentials: true }
      );
      await axios.post(
        `${apiPrefix}/checkAndSaveEmailsForScheduledSearch`,
        {
          user_id: userId,
          scheduled_search_id: searchData?.id,
          email_addresses: emailTags.map(t => t.text),
        },
        { withCredentials: true }
      );
      await axios.post(
        `${apiPrefix}/checkAndSaveExcludingTermsForScheduledSearch`,
        {
          user_id: userId,
          scheduled_search_id: searchData?.id,
          terms: excludingTags.map(t => ({ id: String(t.id ?? t.text ?? ''), text: String(t.text ?? '') })),
        },
        { withCredentials: true }
      );
      showSuccess('Promjene uspješno spremljene');
    } catch (err) {
      console.error("Greška prilikom spremanja:", err);
      showError('Greška pri spremanju. Pokušajte ponovo.');
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f7fa' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f7fa' }}>
      <Box sx={{ maxWidth: 800, mx: 'auto', px: 2, py: 3 }}>
        {/* Page header */}
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#2c3e50', mb: 3 }}>
          Uredi pretragu
        </Typography>

        <Paper sx={{ p: 3, borderRadius: 2 }}>
          {/* Name & Status */}
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#2c3e50', mb: 0.5 }}>Naziv</Typography>
          <TextField
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={!name.trim()}
            helperText={!name.trim() ? 'Naziv je obavezno polje' : ''}
            size="small"
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#2c3e50' }}>Status:</Typography>
            <Typography
              variant="caption"
              sx={{
                px: 1, py: 0.25, borderRadius: 1, fontWeight: 600,
                backgroundColor: searchData?.is_active ? '#e8f5e9' : '#f5f5f5',
                color: searchData?.is_active ? '#27ae60' : '#999',
              }}
            >
              {searchData?.is_active ? 'Aktivna' : 'Neaktivna'}
            </Typography>
            <Switch
              size="small"
              checked={!!searchData?.is_active}
              onChange={() => handleToggle(searchData)}
              color="primary"
            />
          </Box>

          {/* Schedule */}
          <SectionHeader title="Učestalost" />
          <FormControl>
            <RadioGroup
              row
              value={frequency}
              onChange={(e) => setFrequency(e.target.value)}
            >
              <FormControlLabel value="daily" control={<Radio />} label="Svakodnevno" />
              <FormControlLabel value="weekly" control={<Radio />} label="Tjedno" />
            </RadioGroup>
          </FormControl>
          <Box sx={{ display: 'flex', gap: 3, mt: 2, flexWrap: 'wrap' }}>
            {frequency === 'weekly' && (
              <Box>
                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600, color: '#2c3e50' }}>
                  Dan u tjednu
                </Typography>
                <Select value={executionDay} onChange={(e) => setExecutionDay(e.target.value)} size="small" sx={{ minWidth: 160 }}>
                  {dayLabels.map((label, i) => (
                    <MenuItem key={i} value={i}>{label}</MenuItem>
                  ))}
                </Select>
              </Box>
            )}
            <Box>
              <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600, color: '#2c3e50' }}>
                Vrijeme pokretanja
              </Typography>
              <TextField
                type="time"
                value={executionTime}
                onChange={(e) => setExecutionTime(e.target.value)}
                inputProps={{ step: 300 }}
                size="small"
              />
            </Box>
            <Box>
              <Tooltip title="Koliko sati unazad treba pretražiti (npr. 12 = zadnjih 12 sati)" placement="right">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#2c3e50' }}>
                    Pretraži unazad (sati)
                  </Typography>
                  <HelpOutlineIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                </Box>
              </Tooltip>
              <TextField
                type="number"
                value={timeSpan}
                onChange={(e) => setTimeSpan(e.target.value)}
                inputProps={{ min: 1, max: 168 }}
                size="small"
                sx={{ width: 120 }}
              />
            </Box>
          </Box>

          {/* Terms */}
          <SectionHeader title="Pojmovi" />
          <ReactTags
            tags={tags}
            suggestions={[]}
            delimiters={delimiters}
            handleDelete={handleDeleteTag}
            handleAddition={handleAddition}
            handleDrag={handleDrag}
            handleTagClick={() => {}}
            inputFieldPosition="bottom"
            placeholder="Unesi pojam, zatim zarez ili Enter"
            autocomplete
            editable
          />

          {/* Excluding terms */}
          <SectionHeader
            title="Isključujući pojmovi"
            tooltip="Članci koji sadrže ove pojmove neće biti prikazani u rezultatima pretrage"
          />
          <ReactTags
            tags={excludingTags}
            suggestions={[]}
            delimiters={delimiters}
            handleDelete={handleDeleteExcludingTag}
            handleAddition={handleAdditionExcluding}
            handleTagClick={() => {}}
            inputFieldPosition="bottom"
            placeholder="Unesi pojam za isključivanje, zatim zarez ili Enter"
            autocomplete
            editable
          />

          {/* Portals */}
          <SectionHeader title="Portali" />
          <PortalPicker value={selectedPortals} onChange={setSelectedPortals} showChips={false} autoDetectForeign />

          {/* Email addresses */}
          <SectionHeader title="Email adrese za dostavu rezultata" />
          <ReactTags
            tags={emailTags}
            suggestions={[]}
            delimiters={delimiters}
            handleDelete={(i) => setEmailTags(emailTags.filter((_, idx) => idx !== i))}
            handleAddition={(tag) => setEmailTags([...emailTags, tag])}
            handleTagClick={() => {}}
            inputFieldPosition="bottom"
            placeholder="Unesi email adresu, zatim zarez ili Enter"
            autocomplete
            editable
          />

          {/* Action buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 4, pt: 3, borderTop: '1px solid #eee' }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/scheduledSearches')}
            >
              Natrag na listu
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || !isFormValid || !hasChanges}
              startIcon={saving ? <CircularProgress size={16} /> : null}
            >
              {saving ? 'Spremanje...' : 'Spremi promjene'}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default ScheduledSearchDetails;
